import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { ExtractionRequestError } from "@/features/data-curation/api/curation";
import { useDeleteExtractionColumn } from "@/features/data-curation/hooks/useCuration";
import type { CurationColumn } from "@/features/data-curation/types";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

type DeleteColumnDialogProps = {
  datasetId: string;
  revision: number;
  /** All the columns of the dataset, also the ones that a filter hides. */
  columnCount: number;
  agentWorking: boolean;
  /** The column to delete; the dialog is open while it is set. */
  column: CurationColumn | null;
  onClose: () => void;
};

const deleteErrorMessage = (error: Error) =>
  error instanceof ExtractionRequestError && /revision conflict/i.test(error.message)
    ? "The dataset changed while you were working. Try again."
    : error.message;

export const DeleteColumnDialog = ({
  datasetId,
  revision,
  columnCount,
  agentWorking,
  column,
  onClose,
}: DeleteColumnDialogProps) => {
  const deleteColumn = useDeleteExtractionColumn(datasetId);
  const lastColumn = columnCount <= 1;
  const close = () => {
    deleteColumn.reset();
    onClose();
  };
  const confirm = () => {
    if (!column) return;
    deleteColumn.mutate(
      { columnId: column.column_id, expectedRevision: revision },
      {
        onSuccess: () => {
          toast.success(`Deleted the column ${column.label}.`);
          close();
        },
      },
    );
  };
  return (
    <Dialog
      open={column != null}
      onOpenChange={(open) => {
        if (!open && !deleteColumn.isPending) close();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete the column {column?.label}?</DialogTitle>
          <DialogDescription>
            {column?.value_type} · {column?.required ? "required" : "optional"}
          </DialogDescription>
        </DialogHeader>
        {lastColumn ? (
          <p className="text-sm text-zinc-600">
            A dataset needs at least one column. Add another column before you
            delete this one.
          </p>
        ) : (
          <div className="space-y-2 text-sm text-zinc-600">
            <p>
              The cells of this column and their evidence leave the grid. The
              other columns, the rows, and their verdicts stay.
            </p>
            <p>
              Saved checkpoints keep the cells. Restoring a checkpoint brings the
              column back.
            </p>
            {agentWorking && (
              <p>
                The agent is working. If it writes to this column, the write fails
                and the agent reads the columns again.
              </p>
            )}
          </div>
        )}
        {deleteColumn.error && (
          <p className="rounded-[3px] bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
            {deleteErrorMessage(deleteColumn.error)}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={deleteColumn.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            loading={deleteColumn.isPending}
            disabled={lastColumn}
            aria-label={deleteColumn.isPending ? "Deleting column" : undefined}
            onClick={confirm}
          >
            <Trash2 className="h-4 w-4" aria-hidden />
            Delete column
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
